import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

/**
 * Control Room → Partners tab (Partner Link, Jenn 2026-10-07). Therapists
 * and practitioners, kept apart from affiliates. New applications show in
 * the "Waiting on you" box above and in Team365 To-Do's (Approve / Not now); here is everyone approved, where
 * their page stands, their 10% choice, and how to pay them. Opening one
 * goes to the partner's own page (never the affiliate one).
 */
const money = (n) => `$${Number(n || 0).toFixed(2)}`;

const pageStatus = (p) => {
  if (p.profile_submitted_at && p.landing_page_approved_at) return { label: 'Live', cls: 'bg-reset-sage/10 text-reset-sage' };
  if (p.profile_submitted_at) return { label: 'Page waiting on you', cls: 'bg-reset-terracotta/10 text-reset-terracotta' };
  return { label: 'Page info not sent yet', cls: 'bg-reset-gold/25 text-reset-brown' };
};

const payoutText = (p) => {
  if (p.give_to_clients) return 'Gives the 10% to clients as a discount';
  if (!p.payout_method) return 'Takes the 10% · payment details not added yet';
  const how = p.payout_method === 'paypal' ? 'PayPal' : 'e-Transfer';
  return `Takes the 10% · ${how} to ${p.payout_email || '(no email yet)'}`;
};

export default function PartnersView() {
  const navigate = useNavigate();
  const [partners, setPartners] = useState(null);
  const [error, setError] = useState('');

  useEffect(() => {
    api.get('/admin/partners')
      .then(r => setPartners(r.data.partners))
      .catch(() => setError('Could not load partners.'));
  }, []);

  return (
    <section>
      <div className="flex items-end justify-between flex-wrap gap-3 mb-4">
        <div>
          <h2 className="font-serif text-xl text-reset-brownDeep">Partners</h2>
          <p className="text-sm text-gray-500">Therapists and practitioners who share 365reset with their clients. They don't need to be members.</p>
        </div>
        <a href="https://365reset.com/partner-link.html" target="_blank" rel="noopener noreferrer" className="text-sm font-bold text-reset-terracotta">See the public Partner page →</a>
      </div>

      {error && <p className="text-sm text-reset-terracotta">{error}</p>}
      {partners === null && !error && <p className="text-sm text-gray-500">Loading…</p>}
      {partners?.length === 0 && (
        <div className="bg-white border border-reset-creamDark rounded-2xl p-6 text-sm text-gray-500">
          No partners yet. When someone applies on the Partner page, they'll show up in the Waiting on you box above, and in your Team365 To-Do's, for you to approve.
        </div>
      )}

      {partners?.length > 0 && (
        <ul className="space-y-3">
          {partners.map(p => {
            const st = pageStatus(p);
            return (
              <li key={p.id} className="bg-white border border-reset-creamDark rounded-2xl p-5 flex flex-wrap items-center gap-4">
                <div className="flex-1 min-w-[240px]">
                  <p className="font-bold text-reset-brownDeep">
                    {p.name}{p.practice_name ? <span className="font-normal text-gray-500"> · {p.practice_name}</span> : null}
                    {p.is_active === false && <span className="ml-2 text-xs text-gray-400">(inactive)</span>}
                  </p>
                  <p className="text-xs text-gray-500">{p.email}</p>
                  <p className="text-sm text-reset-brown mt-1">{payoutText(p)}</p>
                </div>
                <div className="text-sm text-reset-brown text-right">
                  <span className={`inline-block text-xs font-bold px-2.5 py-1 rounded-full mb-1 ${st.cls}`}>{st.label}</span>
                  <p>{p.referral_count || 0} joined · {money(p.owed)} owed · {money(p.paid)} paid</p>
                </div>
                <button onClick={() => navigate(`/admin/partners/${p.id}`)}
                  className="px-4 py-2 bg-reset-terracotta text-white text-sm font-bold rounded-full shrink-0">Open</button>
              </li>
            );
          })}
        </ul>
      )}
      <PartnerMaterials />
    </section>
  );
}

// Partner materials (Jenn, 2026-10-09): the words every partner copies from
// their dashboard — same engine as the affiliate Content Library captions,
// but a separate set, edited here so partners and affiliates never mix.
const MATERIAL_LABELS = {
  facebook: 'Facebook post', instagram: 'Instagram post', linkedin: 'LinkedIn post',
  email: 'Email to their clients', newsletter: 'Newsletter piece', brochure: 'Printable brochure',
};

function PartnerMaterials() {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [adding, setAdding] = useState({ platform: 'facebook', bodyText: '' });
  const load = () => api.get('/admin/partners/materials').then(r => setData(r.data)).catch(() => setError('Could not load partner materials.'));
  useEffect(() => { load(); }, []);

  const add = async () => {
    if (!adding.bodyText.trim()) return;
    try { await api.post('/admin/partners/materials', adding); setAdding({ ...adding, bodyText: '' }); load(); }
    catch (err) { setError(err.response?.data?.error || 'Could not add this.'); }
  };

  if (!data) return error ? <p className="text-sm text-red-600 mt-8">{error}</p> : null;
  return (
    <div className="mt-10">
      <h3 className="text-lg font-bold text-reset-brownDeep mb-1">Partner materials</h3>
      <p className="text-sm text-gray-500 mb-4">What partners copy from their dashboard. Their own link is added automatically. The brochure's first line is its heading; lines starting with "- " become a list.</p>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {data.types.map(type => (
        <div key={type} className="mb-6">
          <p className="text-xs font-bold uppercase tracking-wide text-reset-terracotta mb-2">{MATERIAL_LABELS[type] || type}</p>
          <div className="space-y-3">
            {data.materials.filter(m => m.platform === type).map(m => <MaterialRow key={m.id} item={m} onChanged={load} onError={setError} />)}
            {!data.materials.some(m => m.platform === type) && <p className="text-sm text-gray-400">None yet.</p>}
          </div>
        </div>
      ))}
      <div className="bg-white border border-reset-creamDark rounded-2xl p-5">
        <p className="text-sm font-bold text-reset-brownDeep mb-2">Add a new one</p>
        <select value={adding.platform} onChange={e => setAdding({ ...adding, platform: e.target.value })}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2">
          {data.types.map(t => <option key={t} value={t}>{MATERIAL_LABELS[t] || t}</option>)}
        </select>
        <textarea value={adding.bodyText} onChange={e => setAdding({ ...adding, bodyText: e.target.value })} rows={4}
          className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2" placeholder="The words partners will copy" />
        <button onClick={add} className="px-4 py-2 bg-reset-terracotta text-white text-sm font-bold rounded-full">Add</button>
      </div>
    </div>
  );
}

function MaterialRow({ item, onChanged, onError }) {
  const [text, setText] = useState(item.body_text);
  const [saved, setSaved] = useState(false);
  const save = async () => {
    try { await api.put(`/admin/partners/materials/${item.id}`, { bodyText: text }); setSaved(true); setTimeout(() => setSaved(false), 1500); onChanged(); }
    catch (err) { onError(err.response?.data?.error || 'Could not save this.'); }
  };
  const remove = async () => {
    if (!window.confirm('Delete this from partner materials?')) return;
    try { await api.delete(`/admin/partners/materials/${item.id}`); onChanged(); }
    catch (err) { onError(err.response?.data?.error || 'Could not delete this.'); }
  };
  return (
    <div className="bg-white border border-reset-creamDark rounded-2xl p-4">
      <textarea value={text} onChange={e => setText(e.target.value)} rows={Math.min(12, Math.max(3, text.split('\n').length + 1))}
        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm mb-2" />
      <div className="flex gap-2 items-center">
        <button onClick={save} disabled={text.trim() === item.body_text.trim()} className="px-4 py-1.5 bg-reset-terracotta text-white text-xs font-bold rounded-full disabled:opacity-40">Save</button>
        <button onClick={remove} className="px-4 py-1.5 border border-gray-200 text-xs font-bold rounded-full text-gray-500">Delete</button>
        {saved && <span className="text-xs text-reset-sage">Saved</span>}
      </div>
    </div>
  );
}
